import angular, { IPromise, IScope } from 'angular'
import Shepherd from 'tether-shepherd'
import { VdTracking } from '@vend/vend.js'
import { STATES } from '../../../../lib/routes'
import {
  $document,
  $rootScope,
  $q,
  $state,
  $timeout,
} from '../../ng-compat/imports'
import { TrainingModeManager } from '../../managers/training-mode-manager'
import { SaleManager, SaleManagerEvents } from '../../managers/sale-manager'
import { EVENT, REACT_TRAINING_MODE } from '../../constants'
import { getFeatureFromCache } from '../../util/feature-cache'

/**
 * Currently events are being migrated away from $rootScope and this is a list
 * of events that are now emitted from the SaleManager.
 */
const saleManagerEvents = [EVENT.SALE_ADD_LINE_ITEM]

/**
 * Determines if an event is a SaleManager event
 * Needs to act as a type predicate because actually the SaleManger events
 * are a mix of strings & enums :(
 */
const isSaleManagerEvent = (
  event: string
): event is keyof SaleManagerEvents => {
  return (saleManagerEvents as string[]).includes(event)
}

/**
 * Defines the Training Tour (to pair with web register Training Mode)
 *
 * Provides a wrapper for Shepherd.js (http://github.hubspot.com/shepherd/docs/welcome/) and hand-holds the user through
 * the basic sale steps of web register.
 *
 * @module webregister.directives.trainingTour
 */
angular.module('webregister.directives').directive('trainingTour', function () {
  return {
    restrict: 'E',
    scope: true,
    bindToController: true,
    controllerAs: 'trainingTourCtrl',

    /**
     * =======================================
     * TRAINING TOUR CONTROLLER
     * =======================================
     */
    controller(
      this: $TSFixMe,
      $scope: IScope,
      trainingModeManager: TrainingModeManager,
      saleManager: SaleManager,
      vdTracking: VdTracking
    ) {
      // ============================== Private API ======================== //
      const reactTrainingTour = getFeatureFromCache(REACT_TRAINING_MODE)
      if (reactTrainingTour) {
        // If React version of training tour is enabled, do not proceed with AngularJS version
        return
      }
      /**
       * Creates a new Shepherd tour based on the steps provided by the training mode manager.
       *
       * @private
       * @method _initiateTour
       */
      this._initiateTour = function () {
        this.tour = new Shepherd.Tour({
          defaults: {
            classes: 'wr-tour',
            buttons: [],
            showCancelLink: false,
          },
        })
        trainingModeManager.getTourSteps().then((steps: $TSFixMe) => {
          this._bindSteps(steps)

          const transitionToSaleRoute = !$state.includes(STATES.SALE)
            ? (($state.go(STATES.SALE) as unknown) as IPromise<void>)
            : $q.resolve()

          // Support initiating the tour from step 1 from anywhere
          transitionToSaleRoute.then(() => {
            $scope.$applyAsync(() => this.tour.start())
          })
        })
      }

      /**
       * Bind a single trigger event for the specified step with optionally configured delay on show.
       *
       * @method _bindStepTrigger
       *
       * @param  options {Object}
       *
       *         @param stepId {String}
       *                The id of the step to bind trigger event for
       *
       *         @param triggerEvent {String}
       *                The configured trigger event
       *
       *         @param [stepDelay=200] {Number}
       *                Optionally configured step delay
       *
       *         @param [trackingData] {Object}
       *                Optionally configured tracking data
       *         @param [isSaleManagerEvent] {Boolean}
       *             Optionally configured if the event is a sale manager event
       *
       * @return {Function} Returns the method to be used when unbinding the globally bound trigger event on destroy
       */
      this._bindStepTrigger = function (options: {
        stepId: string
        triggerEvent: string
        stepDelay?: number
        trackingData?: { [key: string]: { step: string } }
        isSaleManagerEvent?: boolean
      }) {
        const handler = () => {
          const stepDelay = options.stepDelay || 200
          const trackingData =
            options.trackingData && options.trackingData[options.triggerEvent]

          // Track the step if there is data available for the specific event
          if (trackingData) {
            vdTracking.trackUserEvent({
              eventName: trainingModeManager.currentTourType!,
              metadata: trackingData,
            })
          }

          $timeout(() => {
            // Handles cases where tour has not been destroyed quite yet when a trigger event happens
            if (this.tour) {
              this.tour.show(options.stepId)
            }
          }, stepDelay)
        }

        if (isSaleManagerEvent(options.triggerEvent)) {
          const triggerEvent = options.triggerEvent
          saleManager.on(triggerEvent, handler)
          return () => {
            saleManager.off(triggerEvent, handler)
          }
        }

        return $rootScope.$on(options.triggerEvent, handler)
      }

      /**
       * Adds each step to the tour, binding the step trigger.
       *
       * Also applies a timeout delay before showing the tip to account for any content that is dynamically added / animated.
       *
       * @private
       * @method _bindSteps
       * @param tourSteps {Array} The config for the tour steps that should be bound.
       */
      this._bindSteps = function (tourSteps: $TSFixMe[]) {
        const numberOfSteps = tourSteps.length

        tourSteps.forEach((stepDetails, index) => {
          // Ensure we don't change the original step details so subsequent tours are not affected
          const stepConfig: $TSFixMe = {
            id: stepDetails.id,
            attachTo: stepDetails.attachTo,
            tetherOptions: stepDetails.tetherOptions,
          }
          const stepNumber = index + 1

          // Add next button config
          if (stepDetails.nextBtn) {
            stepConfig.buttons = [
              {
                text: 'Okay, thanks!',
                action: this.tour.next,
              },
            ]
          }

          // Add pagination
          if (stepDetails.title && stepDetails.pagination) {
            stepConfig.title = stepDetails.title
              .concat(`<span class="wr-tour-title-pagination vd-text-supplementary">
              ${stepNumber}/${numberOfSteps}</span>`)
          } else {
            stepConfig.title = stepDetails.title
          }

          // Add action text
          if (stepDetails.actionText) {
            stepConfig.text = stepDetails.text
              .concat(`<div class="wr-tour-content-action">
              <i class="wr-tour-content-action-icon vd-i vd-i-walk"></i>
              ${stepDetails.actionText}</div>`)
          } else {
            stepConfig.text = stepDetails.text
          }

          // Add step to the tour via config
          this.tour.addStep(stepConfig)

          // Once added grab the actual tour step
          const stepIndex = this.tour.steps.indexOf(
            this.tour.getById(stepConfig.id)
          )
          const step = this.tour.steps[stepIndex]

          // Bind step's triggerOn listener so it shows when its trigger action is performed
          if (stepDetails.triggerOn) {
            // Sometimes more than one trigger event is configured
            if (Array.isArray(stepDetails.triggerOn)) {
              step.unbind = []
              stepDetails.triggerOn.forEach((trigger: $TSFixMe) => {
                const unbind = this._bindStepTrigger({
                  stepId: stepConfig.id,
                  triggerEvent: trigger,
                  stepDelay: stepDetails.delay,
                  trackingData: stepDetails.trackingData,
                  isSaleManagerEvent: stepDetails.isSaleManagerEvent,
                })

                step.unbind.push(unbind)
              }, this)
            } else {
              // Ensure the unbind method is saved to the actual step
              step.unbind = this._bindStepTrigger({
                stepId: stepConfig.id,
                triggerEvent: stepDetails.triggerOn,
                stepDelay: stepDetails.delay,
                trackingData: stepDetails.trackingData,
              })
            }
          }
        })
      }

      /**
       * Destroys each step, removing it from the DOM. Unbinds the current step event listener.
       *
       * @private
       * @method _destroyTour
       */
      this._destroyTour = function () {
        if (this.tour) {
          $document.off('mousedown', this._hideCurrentTourStep)
          this.tour.steps.forEach((step: $TSFixMe) => {
            const unbind = step.unbind

            // @note unbind is only defined if triggerOn event is configured
            if (unbind) {
              // @note supports multiple trigger events being bound & unbound
              if (Array.isArray(unbind)) {
                unbind.forEach(unbindMethod => unbindMethod())
              } else {
                unbind()
              }
            }

            step.destroy()
          })
          this.tour = null
        }
      }

      /**
       * Listens for changes in posStateManager trainingTour.
       *
       * @private
       * @method _onTourChange
       */
      this._onTourChange = function (event: $TSFixMe) {
        if (trainingModeManager.tourEnabled) {
          this._initiateTour()
        } else {
          this._destroyTour()
        }
      }

      // @todo the click outside detection functionality should really be outside of this method?
      /**
       * Hides the current tour step.
       *
       * Only hides it if the captured event target is not within a Shepherd step element or if event parameter is not
       * passed
       *
       * @private
       * @method _hideCurrentTourStep
       *
       * @param  {Event} [event]
       *         The event captured when the document has been clicked
       */
      this._hideCurrentTourStep = (event?: $TSFixMe) => {
        const isElementShepherdChild =
          event && Boolean(event.target.closest('.shepherd-step'))

        if (!isElementShepherdChild) {
          this.tour.hide()
        }
      }

      // Listen for changes in the training tour
      trainingModeManager.on('tourEnabledChange', this._onTourChange, this)

      // Destroy listener
      $scope.$on('$destroy', () => {
        this._destroyTour()
        trainingModeManager.off('tourEnabledChange', this._onTourChange)
      })
    },
  }
})
